import * as Styled from "./CheckMatch.styles";

import {
  GetCheckMatchPropsResponse as CheckMatchDataProps,
  MenuGroup,
} from "utilities/getCheckMatch";
import { CloseOutlined, SaveOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useState } from "react";

import CheckMatchMappingButtons from "components/CheckMatchMappingButtons/CheckMatchMappingButtons";
import isDifferent from "utilities/isDifferent";
import {
  useCheckMatch,
  CheckMatchInitDataProps,
} from "context/CheckMatchContext";
import { useParams } from "react-router-dom";
import { SearchBar } from "components/SearchBar/SearchBar";

function CheckMatch() {
  const [save, setSave] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [initialCloseOfDayHour, setInitialCloseOfDayHour] = useState<number>(4);
  const [newCloseOfDayHour, setNewCloseOfDayHour] = useState<
    number | undefined
  >();
  const [initialTimeZone, setInitialTimeZone] =
    useState<string>("America/New_York");
  const [newTimeZone, setNewTimeZone] = useState<string | undefined>();

  const { posGUID } = useParams();

  const {
    cancelChanges,
    checkMatchData,
    initialMappingState,
    isCheckMatchMappingsSaving,
    isCheckMatchSaving,
    mappingState,
    updateCheckMatch,
    initCheckMatch,
    updateCheckMatchMappings,
    updateMappingState,
  } = useCheckMatch();

  const checkMatchMap = checkMatchData?.checkMatchMenuGroupMap ?? {};

  const checkMatchMapArray = Object.keys(checkMatchMap).map((key) => {
    const item = checkMatchMap[key];

    return {
      name: item.name,
      key,
      item,
    };
  });

  // --- Pagination constants + state (client-side) ---
  const MAX_PER_PAGE = 100;
  const totalItems = checkMatchMapArray.length;
  const initialPageSize = Math.min(totalItems || 10, MAX_PER_PAGE);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: initialPageSize,
    total: totalItems,
  });

  // Search
  const filteredCheckMatchMapArray = checkMatchMapArray.filter(
    (group) =>
      typeof group.name === "string" &&
      group.name.toLowerCase().includes(searchValue.toLowerCase())
  );
  const visibleItems = filteredCheckMatchMapArray.length;

  // Reset pagination when the visible (filtered) items change (e.g., search updates)
  // ensures page doesn't point beyond the filtered result and pageSize updates
  useEffect(() => {
    const desiredPageSize = Math.min(visibleItems || 10, MAX_PER_PAGE);
    setPagination((prev) => {
      if (prev.total !== visibleItems) {
        return { current: 1, pageSize: desiredPageSize, total: visibleItems };
      }
      if (prev.pageSize !== desiredPageSize) {
        return { current: 1, pageSize: desiredPageSize, total: visibleItems };
      }
      return { ...prev, total: visibleItems };
    });
  }, [visibleItems]);

  // keep pagination in sync when location changes (reset)
  useEffect(() => {
    if (posGUID) {
      setInitialCloseOfDayHour(4);
      setInitialTimeZone("America/New_York");
      setNewCloseOfDayHour(undefined);
      setNewTimeZone(undefined);

      // reset pagination when location changes
      setPagination((prev) => ({
        ...prev,
        current: 1,
        pageSize: Math.min(totalItems || 10, MAX_PER_PAGE),
        total: totalItems,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posGUID]); // keep posGUID only - paging will sync in separate effect

  // sync pagination when data length changes
  useEffect(() => {
    const desiredPageSize = Math.min(totalItems || 10, MAX_PER_PAGE);
    setPagination((prev) => {
      // if total changes, reset to first page to avoid out-of-range pages
      if (prev.total !== totalItems) {
        return { current: 1, pageSize: desiredPageSize, total: totalItems };
      }

      // otherwise just ensure pageSize clamps to desired
      if (prev.pageSize !== desiredPageSize) {
        return {
          ...prev,
          pageSize: desiredPageSize,
          total: totalItems,
          current: 1,
        };
      }

      return { ...prev, total: totalItems };
    });
  }, [totalItems]);

  // reset new states when locationId changes
  useEffect(() => {
    if (posGUID) {
      setInitialCloseOfDayHour(4);
      setInitialTimeZone("America/New_York");
      setNewCloseOfDayHour(undefined);
      setNewTimeZone(undefined);
    }
  }, [posGUID]);

  useEffect(() => {
    if (checkMatchData?.closeOfDayHour) {
      setInitialCloseOfDayHour(checkMatchData?.closeOfDayHour);
    }
  }, [checkMatchData?.closeOfDayHour]);

  useEffect(() => {
    if (checkMatchData?.timeZone) {
      setInitialTimeZone(checkMatchData?.timeZone);
    }
  }, [checkMatchData?.timeZone]);

  useEffect(() => {
    const isMapDifferent =
      initialMappingState &&
      mappingState &&
      isDifferent(initialMappingState, mappingState);

    if (isMapDifferent) {
      setSave(true);
    } else {
      setSave(false);
    }
  }, [mappingState, initialMappingState]);

  const handleMapping = (menuGroupId: string, value: string) => {
    updateMappingState(menuGroupId, value);
  };

  const handleSave = useCallback(() => {
    updateCheckMatchMappings();
  }, [updateCheckMatchMappings]);

  const handleCancel = useCallback(() => {
    cancelChanges();
    setSave(false);
  }, [cancelChanges]);

  const handleCloseOfDayHourChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.value) {
        setNewCloseOfDayHour(parseInt(e.target.value));
      }
    },
    []
  );

  const handleTimeZoneChange = useCallback((e: any) => {
    setNewTimeZone(e.toString());
  }, []);

  const handleSaveCheckMatchSettings = useCallback(() => {
    if (!checkMatchData) {
      const newCheckMatchData: CheckMatchInitDataProps = {
        closeOfDayHour: newCloseOfDayHour ?? initialCloseOfDayHour ?? 4,
        timeZone: newTimeZone ?? initialTimeZone,
      };
      initCheckMatch(newCheckMatchData);
      return;
    }

    const newCheckMatchData: CheckMatchDataProps = {
      ...checkMatchData,
      closeOfDayHour: newCloseOfDayHour ?? initialCloseOfDayHour ?? 4,
      timeZone: newTimeZone ?? initialTimeZone,
    };

    updateCheckMatch(newCheckMatchData);
  }, [
    checkMatchData,
    initialCloseOfDayHour,
    initialTimeZone,
    newCloseOfDayHour,
    newTimeZone,
    updateCheckMatch,
    initCheckMatch,
  ]);

  const columns = [
    {
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span>Menu Group</span>
          <SearchBar
            allowClear={true}
            placeholder="Search"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
        </div>
      ),
      dataIndex: "name",
      key: "name",
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: "Mapping",
      key: "item",
      dataIndex: "item",
      render: (item: MenuGroup) => {
        return (
          <CheckMatchMappingButtons
            item={item}
            mappingState={mappingState}
            onHandleMapping={handleMapping}
          />
        );
      },
    },
  ];

  // table change handler (AntD pagination object)
  const handleTableChange = (pg: any) => {
    const { current = 1, pageSize = pagination.pageSize } = pg;
    // ensure we clamp pageSize to MAX_PER_PAGE
    const effectivePageSize = Math.min(pageSize, MAX_PER_PAGE);
    setPagination({ current, pageSize: effectivePageSize, total: totalItems });
  };

  return (
    <>
      <Styled.ButtonsRow>
        <Styled.FieldContainer>
          <Styled.Label htmlFor="closeOfDayHour">
            Close of Day Hour:
          </Styled.Label>
          <Styled.Input
            id="closeOfDayHour"
            value={newCloseOfDayHour ?? initialCloseOfDayHour}
            onChange={handleCloseOfDayHourChange}
            disabled={isCheckMatchSaving}
          />
        </Styled.FieldContainer>

        <Styled.FieldContainer>
          <Styled.Label htmlFor="timeZone">Time Zone: </Styled.Label>
          <Styled.Select
            id="timeZone"
            value={newTimeZone ?? initialTimeZone}
            style={{ width: 200 }}
            onChange={handleTimeZoneChange}
            disabled={isCheckMatchSaving}
          >
            <Styled.Select.Option value="America/New_York">
              Eastern Time
            </Styled.Select.Option>
            <Styled.Select.Option value="America/Chicago">
              Central Time
            </Styled.Select.Option>
            <Styled.Select.Option value="America/Denver">
              Mountain Time
            </Styled.Select.Option>
            <Styled.Select.Option value="America/Los_Angeles">
              Pacific Time
            </Styled.Select.Option>
            <Styled.Select.Option value="America/Anchorage">
              Alaska Time
            </Styled.Select.Option>
            <Styled.Select.Option value="Pacific/Honolulu">
              Hawaii Time
            </Styled.Select.Option>
          </Styled.Select>
        </Styled.FieldContainer>

        <Styled.Button
          type="default"
          loading={isCheckMatchSaving}
          onClick={handleSaveCheckMatchSettings}
          className="circular-btn"
        >
          {!isCheckMatchSaving && <SaveOutlined />}
        </Styled.Button>

        <Styled.Divider type="vertical" />

        <Styled.Button
          disabled={!save || isCheckMatchMappingsSaving}
          loading={isCheckMatchMappingsSaving}
          onClick={handleSave}
          type="primary"
          icon={<SaveOutlined />}
        >
          Save Mapping Changes
        </Styled.Button>

        <Styled.Button
          disabled={!save || isCheckMatchMappingsSaving}
          onClick={handleCancel}
          type="default"
          icon={<CloseOutlined />}
        >
          Cancel
        </Styled.Button>
      </Styled.ButtonsRow>
      <Styled.Table
        columns={columns}
        dataSource={filteredCheckMatchMapArray}
        rowKey="key"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: visibleItems,
          showSizeChanger: totalItems > MAX_PER_PAGE,
          pageSizeOptions: ["10", "20", "50", String(MAX_PER_PAGE)],
          hideOnSinglePage: true, // will hide pager when everything fits on one page
        }}
        onChange={handleTableChange}
        style={{ marginBottom: "32px" }}
      />
    </>
  );
}

export default CheckMatch;
